/**
 * BatteryBanner - Bannière sponsorisée affichée quand la batterie est faible
 * 
 * Fonctionnalités :
 * - Détection du niveau de batterie via Battery Status API
 * - Affichage quand batterie < 30%
 * - Animation de notification depuis le haut
 * - Lien affilié Amazon vers batterie externe
 * - Fermeture persistante (localStorage)
 * - Support multilingue via i18n
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/components/battery-banner.css';

const BatteryBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [isCharging, setIsCharging] = useState(false);

  // Seuil de batterie pour afficher la bannière
  const BATTERY_THRESHOLD = 0.30 // 30%
  
  // Durée de fermeture de la bannière (7 jours en millisecondes)
  const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;
  
  // Liens affiliés Amazon - Batteries recommandées
  const BATTERY_LINKS = [
    'https://amzn.to/4hXywI7',
    'https://amzn.to/3Xm8B31',
    'https://amzn.to/495elVu',
    'https://amzn.to/4q1iCis',
    'https://amzn.to/4pPlRKt'
  ];
  
  // Sélection aléatoire d'une batterie
  const AMAZON_AFFILIATE_LINK = BATTERY_LINKS[Math.floor(Math.random() * BATTERY_LINKS.length)];

  useEffect(() => {
    // 🔧 MODE DEBUG - Mettre à true pour forcer l'affichage
    const DEBUG_MODE = false; // ⚠️ METTRE À false APRÈS LES TESTS !
    
    if (DEBUG_MODE) {
      console.log('🔋 BatteryBanner: Mode debug activé - Affichage forcé');
      setShowBanner(true);
      setBatteryLevel(0.25); // Simuler 25% de batterie
      return;
    }
    
    // Vérifier si la bannière a été fermée récemment
    const dismissedUntil = localStorage.getItem('batteryBannerDismissedUntil');
    const now = Date.now();
    
    if (dismissedUntil && now < parseInt(dismissedUntil)) {
      // Bannière fermée, ne pas afficher
      return;
    }

    // Vérifier la compatibilité de la Battery Status API
    if (!('getBattery' in navigator)) {
      console.log('Battery Status API non supportée par ce navigateur');
      return;
    }

    // Initialiser la détection de batterie
    navigator.getBattery().then((battery) => {
      // Fonction de mise à jour du statut
      const updateBatteryStatus = () => {
        const level = battery.level;
        const charging = battery.charging;
        
        setBatteryLevel(level);
        setIsCharging(charging);

        // Afficher la bannière uniquement si la batterie est réellement faible
        setShowBanner(level < BATTERY_THRESHOLD);
      };

      // Initialiser le statut
      updateBatteryStatus();

      // Écouter les changements de niveau de batterie
      battery.addEventListener('levelchange', updateBatteryStatus);
      battery.addEventListener('chargingchange', updateBatteryStatus);

      // Nettoyage des listeners
      return () => {
        battery.removeEventListener('levelchange', updateBatteryStatus);
        battery.removeEventListener('chargingchange', updateBatteryStatus);
      };
    }).catch((error) => {
      console.error('Erreur lors de l\'accès à la Battery Status API:', error);
    });
  }, []);

  // Fermer la bannière et sauvegarder dans localStorage
  const handleDismiss = () => {
    const dismissUntil = Date.now() + DISMISS_DURATION;
    localStorage.setItem('batteryBannerDismissedUntil', dismissUntil.toString());
    setShowBanner(false);
  };

  // Gérer le clic sur le lien affilié
  const handleAffiliateClick = () => {
    // Pas de tracking analytics
  };

  if (!showBanner || isCharging) {
    return null;
  }

  const batteryPercentage = batteryLevel ? Math.round(batteryLevel * 100) : 0;

  return (
    <div className="battery-banner" role="banner" aria-live="polite">
      <div className="battery-banner-content">
        <div className="battery-banner-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z" fill="#ff4444"/>
            <path d="M13 13h-2v5h2v-5zm0-8h-2v5h2V5z" fill="#fff"/>
          </svg>
        </div>
        
        <div className="battery-banner-text">
          <p className="battery-banner-title">
            <strong>⚡ {t('batteryBanner.title', 'Batterie faible')} ({batteryPercentage}%)</strong>
          </p>
          <p className="battery-banner-message">
            {t('batteryBanner.message', 'Ne manquez aucune attraction ! Profitez pleinement de votre journée avec une batterie externe.')}
          </p>
        </div>

        <a 
          href={AMAZON_AFFILIATE_LINK}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="battery-banner-cta"
          onClick={handleAffiliateClick}
          aria-label={t('batteryBanner.ctaLabel', 'Voir les batteries externes sur Amazon')}
        >
          <span className="battery-banner-cta-icon">🔋</span>
          <span className="battery-banner-cta-text">
            {t('batteryBanner.cta', 'Voir les batteries')}
          </span>
          <span className="battery-banner-cta-arrow">→</span>
        </a>

        <button 
          className="battery-banner-close"
          onClick={handleDismiss}
          aria-label={t('common.close', 'Fermer')}
          title={t('batteryBanner.dismissTooltip', 'Masquer temporairement')}
        >
          ×
        </button>
      </div>

      <div className="battery-banner-progress">
        <div 
          className="battery-banner-progress-bar"
          style={{ width: `${batteryPercentage}%` }}
          aria-label={`${batteryPercentage}% de batterie restante`}
        />
      </div>
    </div>
  );
};

export default BatteryBanner;
